/**
 * NetworkMonitorEmptyState — empty-state hero shared by every
 * per-chain monitor.
 *
 * Renders a centered card with an oversized chain icon plate, an
 * `<h2>` welcome title (bumped from h3 in commit `b93c5e8` to keep
 * the heading-order ratchet at zero) and a description paragraph.
 *
 * Optional `children` slot for a primary CTA below the description
 * (some chains render a "Connect a wallet" button there).
 */

import type { ReactNode } from 'react';
import type { NetworkColors } from './types';

interface NetworkMonitorEmptyStateProps {
  /** Chain icon, sized 40 (the plate is 80×80, leaves a 20 px ring). */
  icon: ReactNode;
  /** Welcome title (h2). */
  title: string;
  /** Long-form description paragraph. */
  description: string;
  /** Per-chain colour palette. */
  colors: NetworkColors;
  /** Optional CTA / extra content below the description. */
  children?: ReactNode;
}

export function NetworkMonitorEmptyState({
  icon,
  title,
  description,
  colors,
  children,
}: NetworkMonitorEmptyStateProps) {
  return (
    <div
      style={{
        background: colors.lightGradient,
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        padding: '20px',
        marginBottom: '20px',
        textAlign: 'center',
      }}
    >
      <div style={{ padding: '60px 20px' }}>
        <div
          aria-hidden="true"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '20px',
            background: colors.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {icon}
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '8px' }}>
          {title}
        </h2>
        <p
          style={{
            color: 'var(--muted)',
            marginBottom: '32px',
            maxWidth: '450px',
            margin: '0 auto 32px',
            lineHeight: 1.6,
          }}
        >
          {description}
        </p>
        {children}
      </div>
    </div>
  );
}
